import React, { useState, useEffect, useContext } from 'react';
import {
  Grid,
  Typography,
  Container,
  CardMedia,
  Tooltip as MuiToolTip,
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PieChart } from '@mui/x-charts/PieChart';
import { styled } from '@mui/material/styles';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { useAppDispatch } from 'store/hooks';
import { getWellbeingScore, getWellbeingTrackerDetails } from 'store/slices/mcatSlice';
import { getAppointmentConfirmationDetails } from 'store/slices/appointmentSlice';
import { Page } from 'components/Page';
import { ROUTE } from 'route/RouteEnums';
import dayjs from 'dayjs';
import { AuthContext } from 'utils/AuthContext';

const StyledCategory = styled('text')(({ theme }) => ({
  fill: '#344054',
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: '16px',
  fontWeight: 600,
}));

const StyledScore = styled('text')(({ theme }) => ({
  //   fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 24,
}));

const getWellnessScoreColor = (category: string) => {
  if (category === 'In Crisis') return '#F04438';
  else if (category === 'Critical') return '#FF4405';
  else if (category === 'Struggling') return '#EF6820';
  else if (category === 'Vulnerable') return '#F79009';
  else if (category === 'Balanced') return '#EAAA08';
  else if (category === 'Stable') return '#66C61C';
  else return '#12B76A';
};

const getAccordionDetailUI = (score: number) => {
  if (score <= 100) {
    return (
      <ul>
        <li>
          Seek urgent psychiatric help immediately, including emergency services or a crisis hotline
          if necessary.
        </li>
        <li>
          Ensure a safe environment, actively minimizing potential risks for harm (e.g., securing
          firearms, sharps, and medications).
        </li>
        <li>
          Provide a supportive and understanding presence with constant monitoring, maintaining open
          communication.
        </li>
      </ul>
    );
  } else if (score >= 101 && score <= 200) {
    return (
      <ul>
        <li>
          Schedule an urgent appointment with a mental health professional and follow their
          recommendations.
        </li>
        <li>Monitor constantly for safety, ensuring the child does not become isolated.</li>
        <li>
          Prioritize and encourage self-care practices along with relaxation techniques to manage
          stress.
        </li>
      </ul>
    );
  } else if (score >= 251 && score <= 450) {
    return (
      <ul>
        <li>
          Encourage regular psychiatry visits and therapy sessions to address emotional challenges.
        </li>
        <li>Regularly check in to assess emotional well-being and any relationship struggles.</li>
        <li>
          Foster an environment conducive to sharing feelings and exploring coping tools like
          journaling or meditation.
        </li>
      </ul>
    );
  } else if (score >= 451 && score <= 650) {
    return (
      <ul>
        <li>Continue with regular psychiatry visits and therapy sessions to manage challenges.</li>
        <li>
          Maintain regular check-ins with your child to support their day-to-day emotional
          well-being and navigate life&apos;s challenges.
        </li>
        <li>
          Create a supportive environment for sharing feelings and exploring coping strategies, such
          as creative activities.
        </li>
      </ul>
    );
  } else if (score >= 651 && score <= 800) {
    return (
      <ul>
        <li>
          Encourage ongoing psychiatry and/or therapy sessions to maintain balance and work towards
          stability.
        </li>
        <li>
          Reinforce coping mechanisms for occasional disturbances and assist in recognizing and
          managing triggers.
        </li>
        <li>
          Ensure regular check-ins with your child to provide support and maintain emotional
          balance.
        </li>
      </ul>
    );
  } else if (score >= 801 && score <= 900) {
    return (
      <ul>
        <li>Celebrate emotional achievements and acknowledge growth and stability.</li>
        <li>Provide opportunities for positive social interactions to strengthen relationships.</li>
        <li>Encourage sharing their wellness journey, which could include mentoring peers.</li>
      </ul>
    );
  } else {
    return (
      <ul>
        <li>Praise resilience and positive relationship-building.</li>
        <li>
          Motivate towards setting higher wellness goals or exploring new growth opportunities.
        </li>
        <li>
          Promote engagement in community events or activities that enhance well-being and a sense
          of accomplishment.
        </li>
      </ul>
    );
  }
};

const getAccordionText = (score: number) => {
  if (score <= 100) {
    // eslint-disable-next-line max-len
    return 'Experiencing severe distress leading to significant disruption in daily functioning. The ability to manage everyday life is severely impaired. Safety might be a concern, and immediate psychiatric help is required.';
  } else if (score >= 101 && score <= 200) {
    // eslint-disable-next-line max-len
    return 'Approaching severe mental health distress. This tipping point is marked by issues that intensely affect daily life. Functioning is notably impaired, with safety potentially at risk. Prompt psychiatric intervention is necessary.';
  } else if (score >= 201 && score <= 450) {
    // eslint-disable-next-line max-len
    return 'Regularly encountering emotional challenges that are hard to cope with, affecting daily functioning. Emotional struggles are a constant aspect of life, not yet a crisis but still significant. Support, including psychotropic medications and psychotherapy, is often needed to navigate these challenges.';
  } else if (score >= 451 && score <= 650) {
    // eslint-disable-next-line max-len
    return 'Frequently overwhelmed by circumstances, impacting the ability to meet academic and social demands and maintain healthy relationships. This state leads to instability in mental health and a frequent testing of one’s resilience. Counseling or therapy is recommended to manage these challenges. Support through psychotropic medications can be beneficial as well.';
  } else if (score >= 651 && score <= 800) {
    // eslint-disable-next-line max-len
    return 'Exhibiting a healthy mental state, characterized by emotional equilibrium. Effective management of life’s typical ups and downs is evident, though occasional disturbances and stress may occur. Support through counseling or therapy can be beneficial in maintaining this balance. Psychotropic medications can also be helpful in certain clinical situations.';
  } else if (score >= 801 && score <= 900) {
    // eslint-disable-next-line max-len
    return 'Individuals exhibit a consistently good state of mental health. They are able to handle life’s challenges with resilience and maintain positive relationships. It&apos;s a phase of stability and well-being, though not immune to the ordinary stresses of life.';
  } else {
    // eslint-disable-next-line max-len
    return 'Individuals at this level are not just stable but flourishing. They show a strong sense of purpose, resilience in the face of challenges, and maintain robust, positive relationships. It’s a state of optimal mental well-being, where individuals are not just coping but actively thriving in their personal and professional lives.';
  }
};

function PieCenterLabel({ children }: any) {
  return (
    <>
      <StyledCategory x={175} y={160} sx={{ fontSize: '18px' }}>
        {children[1]}
      </StyledCategory>
      <StyledScore x={175} y={190} fontWeight={600} fill={getWellnessScoreColor(children[1])}>
        {children[0]}
      </StyledScore>
    </>
  );
}

const WellnessDashboard = () => {
  const [score, setScore] = useState<any>();
  const [category, setCategory] = useState('');
  const [trackerPeriod, setTrackerPeriod] = useState('6M');
  const [data, setData] = useState<any>();
  const [nextCheckIn, setNextCheckIn] = useState<any>();
  const [showOverAll, setShowOverAll] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    dispatch(getWellbeingScore(user?.patientId))
      .unwrap()
      .then(async (response: any) => {
        if (response?.status === 200) {
          const wellbeingScore = response?.data?.wellnessScore;
          setScore(response?.data?.wellnessScore);
          if (wellbeingScore < 100) {
            setCategory('In Crisis');
          } else if (wellbeingScore > 100 && wellbeingScore < 200) {
            setCategory('Critical');
          } else if (wellbeingScore > 200 && wellbeingScore < 500) {
            setCategory('Struggling');
          } else if (wellbeingScore > 500 && wellbeingScore < 600) {
            setCategory('Vulnerable');
          } else if (wellbeingScore > 600 && wellbeingScore < 800) {
            setCategory('Balanced');
          } else if (wellbeingScore > 800 && wellbeingScore < 900) {
            setCategory('Stable');
          } else if (wellbeingScore > 900 && wellbeingScore < 1000) {
            setCategory('Thriving');
          }
        }
      });

    dispatch(getAppointmentConfirmationDetails(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          const date = new Date(response.data);
          if (new Date(date) > new Date()) {
            const checkInDate = dayjs(date).format('MM/DD/YYYY hh:mm A');
            setNextCheckIn(checkInDate);
          }
        }
      });
  }, [dispatch, user?.patientId]);

  useEffect(() => {
    const data = {
      period: trackerPeriod,
      patientId: user?.patientId,
    };
    dispatch(getWellbeingTrackerDetails(data))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          const year: any = [];
          // eslint-disable-next-line array-callback-return
          response.data.map((d: any) => {
            year.push(d.date.slice(-4));
          });
          const unique = year.filter((item: any, i: any, ar: any) => ar.indexOf(item) === i);
          if (unique.length > 1) {
            setShowOverAll(true);
          }
          setData(response?.data);
        }
      });
  }, [dispatch, trackerPeriod, user?.patientId]);

  const handleTrackerPeriod = (period: string) => {
    setTrackerPeriod(period);
  };

  return (
    <Page backDir={ROUTE.DASHBOARD}>
      <Container component='main' maxWidth='md'>
        <Grid>
          <Grid item sx={{ textAlign: 'center' }} alignItems={'center'}>
            <Box pt='16px' display={'flex'} alignItems={'center'} flexDirection={'column'}>
              <CardMedia
                component={'img'}
                src='../images/wellnessdashboard.svg'
                alt='dashboard'
                sx={{ width: 70, height: 'auto' }}
              />
              <Typography
                variant='h5'
                position={'relative'}
                width='100%'
                sx={{ textTransform: 'capitalize', textAlign: 'center', color: '#1C1F24' }}
              >
                Wellness Dashboard
                <CardMedia
                  component={'img'}
                  src='../images/vector.svg'
                  alt='heading divider'
                  sx={{
                    position: 'absolute',
                    left: { xs: '27%', sm: '40%', md: '30%' },
                    width: { xs: 70, sm: 80, md: 140 },
                    height: 'auto',
                  }}
                />
              </Typography>
              <Typography
                variant='body1'
                sx={{ marginTop: '15px', fontSize: '20px', color: '#1C1F24' }}
              >
                Wellness Wayfinder: Empower Your Child&apos;s Journey with Clarity.
              </Typography>
            </Box>
          </Grid>

          <Grid item>
            <Box
              mt='30px'
              p='20px 14px'
              sx={{
                background: '#fff',
                alignItems: 'center',
                borderRadius: '12px',
                border: '1px solid #EFF4FF',
                boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
              }}
            >
              <Typography variant='h2' color='#1C1F24'>
                Mindweal Wellness Score
              </Typography>

              <Grid
                container
                spacing={0}
                direction='column'
                alignItems='center'
                justifyContent='center'
                mt='20px'
              >
                <Box>
                  <PieChart
                    series={[
                      {
                        data: [
                          { id: 0, value: 50, label: 'i-Wise', color: '#FFFCF5' },
                          { id: 1, value: 20, label: 'Patient Survey', color: '#FFFCF5' },
                          { id: 2, value: 30, label: 'Guardian Survey', color: '#FFFCF5' },
                        ],
                        arcLabel: (item) => `${item.label}`,
                        // highlightScope: { faded: 'series', highlighted: 'item' },
                        // faded: { innerRadius: 70, additionalRadius: 0 },
                        innerRadius: 70,
                        outerRadius: 170,
                        paddingAngle: 5,
                        cornerRadius: 15,
                        startAngle: -90,
                        endAngle: 360,
                        cx: 170,
                        cy: 170,
                      },
                    ]}
                    tooltip={{ trigger: 'none' }}
                    legend={{ hidden: true }}
                    sx={{
                      '.MuiPieArc-root': {
                        strokeWidth: '1.5px',
                      },
                      ' .MuiPieArcLabel-root': {
                        fontSize: '14px',
                        fontWeight: 600,
                        fill: '#475467',
                        wordBreak: 'break-all',
                      },
                      ' .MuiPieArc-series-auto-generated-id-0': {
                        stroke: '#FFC44D',
                      },
                    }}
                    // onClick={handleClick}
                    width={350}
                    height={400}
                  >
                    <PieCenterLabel>
                      {score}
                      {category}
                    </PieCenterLabel>
                  </PieChart>
                </Box>
              </Grid>

              <Box
                width='100%'
                sx={{
                  background: '#F9FAFB',
                  display: 'flex',
                  padding: '8px 0',
                  borderRadius: '8px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                }}
              >
                <Grid container>
                  <Grid
                    item
                    sx={{
                      borderRight: '1px solid #c9cacb',
                      padding: '0 30px',
                      maxWidth: { xs: '33%', sm: 'inherit' },
                      marginBottom: '10px',
                    }}
                  >
                    <Typography color='#F04438' sx={{ fontSize: '14px', fontWeight: '600' }}>
                      0-100
                    </Typography>
                    <Typography color='#2C3446' sx={{ fontSize: '14px', fontWeight: '400' }}>
                      In Crisis
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sx={{
                      borderRight: '1px solid #c9cacb',
                      padding: '0 30px',
                      maxWidth: { xs: '33%', sm: 'inherit' },
                      marginBottom: '10px',
                    }}
                  >
                    <Typography color='#FF4405' sx={{ fontSize: '14px', fontWeight: '600' }}>
                      101-200
                    </Typography>
                    <Typography color='#2C3446' sx={{ fontSize: '14px', fontWeight: '400' }}>
                      Critical
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sx={{
                      borderRight: '1px solid #c9cacb',
                      padding: '0 30px',
                      maxWidth: { xs: '33%', sm: 'inherit' },
                      marginBottom: '10px',
                    }}
                  >
                    <Typography color='#EF6820' sx={{ fontSize: '14px', fontWeight: '600' }}>
                      201-450{' '}
                    </Typography>
                    <Typography color='#2C3446' sx={{ fontSize: '14px', fontWeight: '400' }}>
                      Struggling
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sx={{
                      borderRight: '1px solid #c9cacb',
                      padding: '0 30px',
                      maxWidth: { xs: '33%', sm: 'inherit' },
                      marginBottom: '10px',
                    }}
                  >
                    <Typography color='#F79009' sx={{ fontSize: '14px', fontWeight: '600' }}>
                      451-650
                    </Typography>
                    <Typography color='#2C3446' sx={{ fontSize: '14px', fontWeight: '400' }}>
                      Vulnerable
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sx={{
                      borderRight: '1px solid #c9cacb',
                      padding: '0 30px',
                      maxWidth: { xs: '33%', sm: 'inherit' },
                      marginBottom: '10px',
                    }}
                  >
                    <Typography color='#EAAA08' sx={{ fontSize: '14px', fontWeight: '600' }}>
                      651-800
                    </Typography>
                    <Typography color='#2C3446' sx={{ fontSize: '14px', fontWeight: '400' }}>
                      Balanced
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sx={{
                      borderRight: '1px solid #c9cacb',
                      padding: '0 30px',
                      maxWidth: { xs: '33%', sm: 'inherit' },
                      marginBottom: '10px',
                    }}
                  >
                    <Typography color='#66C61C' sx={{ fontSize: '14px', fontWeight: '600' }}>
                      801-900
                    </Typography>
                    <Typography color='#2C3446' sx={{ fontSize: '14px', fontWeight: '400' }}>
                      Stable
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sx={{
                      padding: '0 10px',
                      maxWidth: { xs: '33%', sm: 'inherit' },
                      marginBottom: '10px',
                    }}
                  >
                    <Typography color='#12B76A' sx={{ fontSize: '14px', fontWeight: '600' }}>
                      901-1000
                    </Typography>
                    <Typography color='#2C3446' sx={{ fontSize: '14px', fontWeight: '400' }}>
                      Thriving
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              <Grid mt={5}>
                <Accordion
                  sx={{ margin: '16px 0!important', boxShadow: 'none', border: '1px solid #ccc' }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color='primary' />}
                    aria-controls='panel1a-content'
                    id='panel1a-header'
                    sx={{
                      '&.Mui-expanded': {
                        background: '#F9FAFB',
                      },
                      background: '#F9FAFB',
                      borderRadius: '0px',
                      boxShadow: 'none',
                    }}
                  >
                    <Typography sx={{ fontSize: '16px', fontWeigth: 500, color: '#1C1F24' }}>
                      {' '}
                      What does My Wellness Score
                      <span
                        style={{
                          color: getWellnessScoreColor(category),
                          padding: '0 0 0 4px',
                        }}
                      >
                        {score}
                      </span>{' '}
                      mean?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color='#1C1F24' mt='24px' mb='30px' fontWeight={600}>
                      {category}:
                    </Typography>
                    <Typography color='#1C1F24' mb='30px'>
                      {getAccordionText(score)}
                    </Typography>
                    <Box
                      sx={{
                        background: '#F8F5FF',
                        padding: '10px',
                        margin: '15px 0',
                        display: 'flex',
                        flexDirection: 'row',
                      }}
                    >
                      <Typography variant='body1' lineHeight={1.5} color='#1C1F24'>
                        <b>Disclaimer:</b>
                        &nbsp;The information provided here is for general understanding and does
                        not replace professional psychiatric consultation. For personalized advice
                        and treatment, please consult with your provider.
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{ margin: '16px 0!important', boxShadow: 'none', border: '1px solid #ccc' }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color='primary' />}
                    aria-controls='panel1a-content'
                    id='panel1a-header'
                    sx={{
                      '&.Mui-expanded': {
                        background: '#F9FAFB',
                      },
                      background: '#F9FAFB',
                      borderRadius: '0px',
                      boxShadow: 'none',
                    }}
                  >
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#1C1F24' }}>
                      {' '}
                      What I can do{' '}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color='#1C1F24' mt='24px' mb='30px' fontWeight={600}>
                      {category}:
                    </Typography>
                    <Typography color='#1C1F24' mb='30px'>
                      {getAccordionDetailUI(score)}
                    </Typography>
                    <Box
                      sx={{
                        background: '#F8F5FF',
                        padding: '10px',
                        margin: '15px 0',
                        display: 'flex',
                        flexDirection: 'row',
                      }}
                    >
                      <Typography variant='body1' lineHeight={1.5} color='#1C1F24'>
                        <b>Disclaimer:</b>
                        &nbsp;These are general suggestions, not a substitute for professional
                        psychiatric or therapeutic advice. Consult your healthcare provider for
                        personalized guidance.
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        <Grid>
          <Box
            mt='30px'
            p='20px 14px'
            sx={{
              background: '#fff',
              alignItems: 'center',
              borderRadius: '12px',
              border: '1px solid #EFF4FF',
              boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
            }}
          >
            <Grid container>
              <Grid item xs={8}>
                <Typography variant='h2' color='#1C1F24'>
                  Wellness Tracker
                </Typography>
                <Box
                  sx={{
                    textAlign: 'center',
                    background: getWellnessScoreColor(category),
                    width: '150px',
                    maxWidth: '153px',
                    borderRadius: '12px',
                    padding: '2px 12px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  {category}
                </Box>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  textAlign: 'end',
                  color: getWellnessScoreColor(category),
                  fontSize: '24px',
                  fontWeight: 'bolder',
                  lineHeight: '26px',
                }}
              >
                {score}
                <Typography color='#1C1F24'>Current Score</Typography>
              </Grid>
            </Grid>
            <Grid>
              <Box
                width='100%'
                sx={{
                  background: '#F9FAFB',
                  display: 'flex',
                  padding: '0',
                  margin: '30px 0',
                  borderRadius: '24px',
                  border: '1px solid #F2F4F7',
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                }}
              >
                <Grid container sx={{ height: '40px' }}>
                  <Grid item sx={{ padding: '1.5px 30px' }}>
                    <MuiToolTip title='Last 90 days'>
                      <Button
                        variant={trackerPeriod === '3M' ? 'contained' : 'text'}
                        sx={{
                          color: trackerPeriod === '3M' ? '#fff' : '#667085',
                          minWidth: {
                            xs: '45px',
                            md: showOverAll ? '130px' : '200px',
                          },
                          maxWidth: { xs: '45px', md: showOverAll ? '130px' : '200px' },
                          minHeight: '37px!important',
                          maxHeight: '37px!important',
                          background: trackerPeriod === '3M' ? '#9F84FF' : 'none',
                          ':hover': {
                            background: '#9F84FF',
                            color: '#fff',
                          },
                        }}
                        onClick={() => handleTrackerPeriod('3M')}
                      >
                        {' '}
                        3M{' '}
                      </Button>
                    </MuiToolTip>
                  </Grid>
                  <Grid item sx={{ padding: '1.5px 30px' }}>
                    <MuiToolTip title='Last 180 days'>
                      <Button
                        variant={trackerPeriod === '6M' ? 'contained' : 'text'}
                        sx={{
                          color: trackerPeriod === '6M' ? '#fff' : '#667085',
                          minWidth: {
                            xs: '45px',
                            md: showOverAll ? '130px' : '200px',
                          },
                          minHeight: '37px!important',
                          maxHeight: '37px!important',
                          maxWidth: { xs: '45px', md: showOverAll ? '130px' : '200px' },
                          background: trackerPeriod === '6M' ? '#9F84FF' : 'none',
                          ':hover': {
                            background: '#9F84FF',
                            color: '#fff',
                          },
                        }}
                        onClick={() => handleTrackerPeriod('6M')}
                      >
                        {' '}
                        6M{' '}
                      </Button>
                    </MuiToolTip>
                  </Grid>
                  <Grid item sx={{ padding: '1.5px 30px' }}>
                    <MuiToolTip title='Last 365 days'>
                      <Button
                        variant={trackerPeriod === '1Y' ? 'contained' : 'text'}
                        sx={{
                          color: trackerPeriod === '1Y' ? '#fff' : '#667085',
                          minWidth: {
                            xs: '45px',
                            md: showOverAll ? '130px' : '200px',
                          },
                          maxWidth: { xs: '45px', md: showOverAll ? '130px' : '200px' },
                          minHeight: '37px!important',
                          maxHeight: '37px!important',
                          background: trackerPeriod === '1Y' ? '#9F84FF' : 'none',
                          ':hover': {
                            background: '#9F84FF',
                            color: '#fff',
                          },
                        }}
                        onClick={() => handleTrackerPeriod('1Y')}
                      >
                        {' '}
                        1Y{' '}
                      </Button>
                    </MuiToolTip>
                  </Grid>
                  {showOverAll && (
                    <Grid item sx={{ padding: '0 30px' }}>
                      <Button
                        variant={trackerPeriod === 'OVERALL' ? 'contained' : 'text'}
                        sx={{
                          color: trackerPeriod === 'OVERALL' ? '#fff' : '#667085',
                          minWidth: { xs: '45px', md: '130px' },
                          maxWidth: { xs: '45px', md: '130px' },
                        }}
                        onClick={() => handleTrackerPeriod('OVERALL')}
                      >
                        {' '}
                        All{' '}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Box>
              {data?.length > 1 ? (
                <ResponsiveContainer width='90%' height={200}>
                  <AreaChart
                    width={730}
                    height={250}
                    data={data}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                        <stop offset='5%' stopColor='#FFC44D' stopOpacity={0.4} />
                        <stop offset='95%' stopColor='#FFFFFF' stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey='date' />
                    <YAxis />
                    <CartesianGrid strokeDasharray='7 7' stroke='#E5E5EF' />
                    <Tooltip />

                    <Area
                      type='linear'
                      dataKey='score'
                      stroke='#FFC44D'
                      fillOpacity={1}
                      fill='url(#colorUv)'
                      dot={{
                        stroke: '#FFC44D',
                        fill: '#FFC44D',
                        strokeWidth: 1,
                        r: 3,
                        strokeDasharray: '',
                      }}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant='h4' textAlign='center' color='#1C1F24'>
                  You have taken only 1 appointment in{' '}
                  {trackerPeriod === '3M'
                    ? '3 Months'
                    : trackerPeriod === '6M'
                    ? '6 Months'
                    : '1 Year'}
                </Typography>
              )}
            </Grid>

            {nextCheckIn ? (
              <Box
                width='100%'
                sx={{
                  background: '#F9FAFB',
                  display: 'flex',
                  padding: '8px 12px',
                  margin: '30px 0',
                  borderRadius: '8px',
                  border: '1px solid #F2F4F7',
                  justifyContent: 'right',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                }}
              >
                <Grid container>
                  <Grid item xs={8}>
                    <Typography color='#1C1F24'>Next Check In</Typography>
                  </Grid>

                  <Grid item xs={4} textAlign={'right'}>
                    <Typography color='#1C1F24'>{nextCheckIn}</Typography>
                  </Grid>
                </Grid>
              </Box>
            ) : null}
          </Box>

          <Grid>
            <Box
              width='100%'
              sx={{
                background: '#FFECC8',
                display: 'flex',
                padding: '30px',
                margin: '30px 0',
                borderRadius: '24px',
                border: '1px solid #F2F4F7',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'stretch',
              }}
            >
              <Grid container>
                <Grid item xs={3}>
                  <CardMedia component={'img'} src='../images/watering.svg' alt='heading divider' />
                </Grid>
                <Grid item xs={9}>
                  <Typography
                    sx={{
                      fontSize: '30px',
                      fontFamily: 'Playfair Display',
                      wordBreak: 'break-word',
                      lineHeight: '55px',
                      fontWeight: '600',
                      color: '#1C1F24',
                    }}
                  >
                    Understanding Your Child&apos;s Wellness Score
                  </Typography>
                  <CardMedia
                    component={'img'}
                    src='../images/vector.svg'
                    alt='heading divider'
                    sx={{ left: '0', width: 150, height: 'auto' }}
                  />
                  <Typography
                    mt={4}
                    sx={{ textAlign: 'justify', color: '#1C1F24', fontSize: '16px' }}
                  >
                    We assess your child&apos;s well-being through a dual approach: MindWeal experts
                    evaluate psychological symptoms during visits, accounting for half of the
                    wellness score. The other half reflects your insights and your child&apos;s
                    perspective, captured through the Kid screen-10 and WHO-5 questionnaires. This
                    comprehensive method ensures a holistic view of your child&apos;s mental health.
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default WellnessDashboard;
